import { apiService } from '../../../shared/services/apiService';
import { getBaseUrls } from '../../../shared/services/apiConfig';

interface LoginCredentials {
    login: string;
    password: string;
    lojaId: number;
    empresa?: string;
}

interface AuthResponse {
    token: string;
    usuario: {
        id: number;
        login: string;
        nome: string;
        lojaId: number;
    };
}

class AuthService {
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        // Se a empresa for fornecida, configura o serviço de API
        if (credentials.empresa) {
            apiService.setCompany(credentials.empresa);
        }

        const loginPayload = {
            login: credentials.login,
            password: credentials.password,
            lojaId: credentials.lojaId
        };

        // Tenta enviar com camelCase
        const response = await apiService.post<any>(
            '/v1/sistema/usuarios/autenticacao',
            loginPayload
        );

        const token = response.token;
        apiService.setToken(token);

        // Decodificar token para obter dados do usuário
        const decoded = this.parseJwt(token);

        const usuario = {
            id: parseInt(decoded.usuarioId || '0'),
            login: decoded.usuario || decoded.unique_name || '',
            nome: decoded.usuario || '',
            lojaId: parseInt(decoded.lojaId || credentials.lojaId.toString())
        };

        return {
            token,
            usuario
        };
    }

    private parseJwt(token: string) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            return JSON.parse(jsonPayload);
        } catch (e) {
            return {};
        }
    }

    logout() {
        apiService.clearToken();
    }

    isAuthenticated(): boolean {
        return !!apiService.getToken();
    }

    getToken(): string | null {
        return apiService.getToken();
    }

    /**
     * Obtém o lojaId do token JWT atual
     * @returns lojaId do usuário logado ou null se não houver token
     */
    getLojaId(): number | null {
        const token = this.getToken();
        if (!token) {
            return null;
        }

        try {
            const decoded = this.parseJwt(token);
            const lojaId = decoded.lojaId;
            if (lojaId) {
                return parseInt(lojaId.toString(), 10);
            }
            return null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Verifica se o token JWT está expirando ou já expirou.
     * @param token O token JWT a ser verificado. Se nulo, usa o token atual.
     * @param bufferMinutes Minutos de buffer antes da expiração para considerar "quase expirando".
     * @returns True se o token está expirando ou expirou, false caso contrário.
     */
    isTokenExpiringOrExpired(token?: string | null, bufferMinutes: number = 5): boolean {
        const currentToken = token || this.getToken();
        if (!currentToken) {
            return true; // Sem token, considera expirado
        }

        const expirationTime = this.getTokenExpiration(currentToken);
        if (expirationTime === null) {
            return true; // Erro ao decodificar, considera expirado
        }

        const currentTime = Date.now() / 1000; // Tempo atual em segundos
        return expirationTime - currentTime < (bufferMinutes * 60);
    }

    /**
     * Extrai o timestamp de expiração (em segundos) de um token JWT.
     * @param token O token JWT. Se nulo, usa o token atual.
     * @returns O timestamp de expiração em segundos, ou null se não puder ser decodificado.
     */
    getTokenExpiration(token?: string | null): number | null {
        const currentToken = token || this.getToken();
        if (!currentToken) {
            return null;
        }
        try {
            const decoded = this.parseJwt(currentToken);
            return decoded.exp || null;
        } catch (e) {
            console.error('[AuthService] Erro ao decodificar token para obter expiração:', e);
            return null;
        }
    }

    /**
     * Verifica se o token é de televisão (gerado via QR code).
     * Tokens de televisão têm o código da TV no claim 'cpf_cnpj'.
     * @param token O token JWT. Se nulo, usa o token atual.
     * @returns True se for token de televisão, false caso contrário.
     */
    isTelevisaoToken(token?: string | null): boolean {
        const currentToken = token || this.getToken();
        if (!currentToken) {
            return false;
        }
        try {
            const decoded = this.parseJwt(currentToken);
            // Tokens de televisão têm o código da TV no claim 'cpf_cnpj'
            const isTV = !!decoded.cpf_cnpj;
            
            return isTV;
        } catch (e) {
            console.warn('[AuthService] ⚠️ Erro ao decodificar token para verificar tipo:', e);
            return false;
        }
    }

    /**
     * Renova o token usando o endpoint HTTP de refresh token.
     * Funciona mesmo com token expirado, pois o backend decodifica o token sem validar expiração.
     * 
     * IMPORTANTE: O backend agora usa lógica robusta para encontrar o código da TV:
     * - Busca em múltiplos claims: cpf_cnpj, CPF_CNPJ, cpfCnpj, CpfCnpj, ClaimTypes.NameIdentifier, sub
     * - Se não encontrar, busca em TODOS os claims por valor de 5 caracteres alfanuméricos
     * 
     * Este método tenta renovar sempre. O backend decide se o token é válido para renovação.
     * 
     * @param tokenAtual O token atual (pode estar expirado). Se não fornecido, usa o token atual.
     * @returns O novo token renovado.
     * @throws Error se a renovação falhar.
     */
    async refreshToken(tokenAtual?: string | null): Promise<string> {
        const currentToken = tokenAtual || this.getToken();
        if (!currentToken) {
            throw new Error('Não há token disponível para renovar');
        }

            try {
                // Obter URLs base (sem usar apiService para evitar dependência circular)
                const baseUrls = getBaseUrls();
                if (baseUrls.length === 0) {
                    throw new Error('Nenhum servidor de API configurado');
                }

                // Tentar cada servidor até conseguir
                let lastError: Error | null = null;
                for (const baseUrl of baseUrls) {
                    try {
                        const fullUrl = `${baseUrl}/v1/banner/televisao/token/refresh`;
                    
                    // Fazer requisição sem Authorization header (endpoint é AllowAnonymous)
                    const response = await fetch(fullUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ tokenAtual: currentToken }),
                    });

                    if (!response.ok) {
                        const errorText = await response.text().catch(() => 'Erro desconhecido');
                        
                        throw new Error(`HTTP ${response.status}: ${errorText}`);
                    }

                    const data = await response.json();
                    const novoToken = data.novoToken;
                    
                    if (!novoToken) {
                        throw new Error('Resposta de refresh token inválida: sem novo token');
                    }

                        // Atualizar o token no apiService e localStorage
                        apiService.setToken(novoToken);
                    
                    return novoToken;
                } catch (error: any) {
                    lastError = error;
                    console.warn(`[AuthService] ⚠️ Erro ao renovar token em ${baseUrl}:`, error.message);
                    // Tentar próximo servidor
                    continue;
                }
            }

            // Se chegou aqui, todos os servidores falharam
            throw lastError || new Error('Falha ao renovar token em todos os servidores');
        } catch (error: any) {
            console.error('[AuthService] ❌ Erro ao renovar token:', error);
            throw new Error(`Falha ao renovar token: ${error.message || 'Erro desconhecido'}`);
        }
    }

    /**
     * Obtém um token de acesso válido, renovando-o automaticamente se estiver expirando.
     * Esta é a função "inteligente" que garante sempre ter um token válido.
     * 
     * IMPORTANTE: 
     * - Para tokens de TELEVISÃO (QR code): renova via HTTP endpoint
     * - Para tokens de USUÁRIO (login): não renova automaticamente (deve usar SignalR TokenRenovado)
     * 
     * @param bufferMinutes Minutos de buffer para considerar o token "quase expirando" (padrão: 5 minutos).
     * @returns Promise<string | null> - O token de acesso válido, ou null se não houver token e não conseguir renovar.
     */
    async getValidAccessToken(bufferMinutes: number = 5): Promise<string | null> {
        let currentToken = this.getToken();

        if (!currentToken) {
            console.warn('[AuthService] ⚠️ Sem token de acesso disponível');
            return null;
        }

        // Renovação proativa via `/v1/banner/televisao/token/refresh` só faz sentido para JWT de TV (QR).
        // Token de usuário (login/gestor): nunca trocar aqui — evita "refresh" a cada request e sessão inválida.
        if (!this.isTelevisaoToken(currentToken)) {
            return currentToken;
        }

        if (this.isTokenExpiringOrExpired(currentToken, bufferMinutes)) {
            try {
                const novoToken = await this.refreshToken(currentToken);
                return novoToken;
            } catch (error: any) {
                const errorMessage = error.message || '';
                const isTokenInvalido = errorMessage.includes('não foi possível extrair o código') || 
                                       errorMessage.includes('Token inválido');
                
                if (isTokenInvalido) {
                    console.log(`[AuthService] ⚠️ Token não pode ser renovado via HTTP. Renovação deve ser feita via SignalR (TokenRenovado).`);
                    // Se já expirou de fato e estamos em totem, SignalR também não vai salvar sozinho a longo prazo —
                    // a UI de totem fará o check periódico; aqui só sinaliza quando já está expirado.
                    if (this.isTokenExpiringOrExpired(currentToken, 0)) {
                        const { notifyTotemSessionExpired } = await import('../../../shared/services/totemSessionRecovery');
                        notifyTotemSessionExpired('token-invalido-expirado');
                    }
                    return currentToken;
                } else {
                    console.error('[AuthService] ❌ Falha ao renovar token:', error);
                    if (this.isTokenExpiringOrExpired(currentToken, 0)) {
                        const { notifyTotemSessionExpired } = await import('../../../shared/services/totemSessionRecovery');
                        notifyTotemSessionExpired('refresh-proativo-falhou');
                    }
                    return currentToken;
                }
            }
        }

        return currentToken;
    }
}

export const authService = new AuthService();
